import { useCallback, useState } from 'react'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Blackout } from '@lms/shared-types'
import { FadeIn } from '../components/primitives/FadeIn'
import { PageHead } from '../components/primitives/PageHead'
import { Modal } from '../components/primitives/Modal'
import { ThinkingDots } from '../components/primitives/ThinkingDots'
import { BlackoutForm } from '../components/hr/BlackoutForm'
import { BlackoutCard } from '../components/hr/BlackoutCard'
import { BlackoutReviewBanner } from '../components/hr/BlackoutReviewBanner'
import type { ReviewItem } from '../components/hr/BlackoutReviewBanner'
import { HrAccessGuard } from '../components/hr/HrAccessGuard'
import {
  deleteBlackout,
  getEscalationTargets,
  listBlackouts,
  listOverlappingLeaves,
} from '../api/blackouts'
import { listTeams } from '../api/teams'
import {
  usePagedList,
  type PagedFetchArgs,
  type PagedFetchResult,
} from '../lib/use-paged-list'
import { PaginationControls } from '../components/lists/PaginationControls'
import { flags } from '../lib/feature-flags'

export function HrBlackoutsPage(): JSX.Element {
  const queryClient = useQueryClient()
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<Blackout | null>(null)

  // Numbered fetcher: /api/v1/blackouts returns a flat array with no server
  // pagination, so we fetch the whole list and slice the requested page in
  // memory. `total` is the full array length — drives the "X–Y of Z" summary
  // and page count.
  //
  // Distinct key from the plain-array ['blackouts'] query used by the global
  // moment-context provider + several dashboards. Sharing the key let that
  // array land in this hook's cache slot, where the paged reader saw a bare
  // array and rendered the empty state despite rows existing. A broad
  // invalidate of ['blackouts'] still cascades here via react-query prefix
  // matching, so refresh-on-save keeps working.
  const fetchBlackoutsPaged = useCallback(
    async ({ page, pageSize }: PagedFetchArgs): Promise<PagedFetchResult<Blackout>> => {
      const all = await listBlackouts()
      return {
        rows: all.slice((page - 1) * pageSize, page * pageSize),
        total: all.length,
      }
    },
    [],
  )

  const pagedList = usePagedList<Blackout>({
    queryKey: ['blackouts', 'hr-list'],
    fetcher: fetchBlackoutsPaged,
  })

  const rows = pagedList.rows
  const isLoading = pagedList.isLoading
  const isError = pagedList.isError

  const teamsQuery = useQuery({ queryKey: ['teams'], queryFn: listTeams })
  const escalationTargetsQuery = useQuery({
    queryKey: ['blackout-escalation-targets'],
    queryFn: getEscalationTargets,
  })

  const teamsById = new Map<string, string>()
  for (const t of teamsQuery.data ?? []) teamsById.set(t.id, t.name)

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteBlackout(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['blackouts'] })
      void queryClient.invalidateQueries({ queryKey: ['calendar'] })
    },
  })

  // --- Re-review banner: aggregate resettable overlapping leaves ---
  const overlappingQueries = useQueries({
    queries: rows.map((b) => ({
      queryKey: ['blackout-overlapping', b.id],
      queryFn: () => listOverlappingLeaves(b.id),
      enabled: flags.blackoutResetPanel,
    })),
  })

  const reviewItems: ReviewItem[] = []
  if (flags.blackoutResetPanel) {
    rows.forEach((b, i) => {
      const result = overlappingQueries[i]
      const items = result?.data ?? []
      for (const item of items) {
        if (item.resettable) {
          reviewItems.push({
            ...item,
            blackoutId: b.id,
            blackoutName: b.name,
            declaredAt: b.createdAt,
          })
        }
      }
    })
  }

  return (
    <HrAccessGuard>
      <div className="page">
      <PageHead
        eyebrow="HR"
        title="Blackout periods"
        subtitle="Critical windows when leave requires Director escalation."
        actions={
          <button
            type="button"
            data-testid="new-blackout"
            onClick={() => setCreating(true)}
            style={{
              padding: '8px 14px',
              borderRadius: 10,
              border: 'none',
              background: 'var(--steel-blue)',
              color: 'var(--surface)',
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            + New blackout
          </button>
        }
      />

      {flags.blackoutResetPanel && reviewItems.length > 0 ? (
        <BlackoutReviewBanner reviewItems={reviewItems} />
      ) : null}

      {isLoading ? (
        <div style={{ padding: 32, textAlign: 'center' }}>
          <ThinkingDots label="Loading blackouts" />
        </div>
      ) : isError ? (
        <div role="alert" style={{ padding: 40, textAlign: 'center', color: 'var(--danger-fg)' }}>
          Couldn’t load blackouts. Check your connection and try again.
        </div>
      ) : rows.length === 0 ? (
        <FadeIn>
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>
            No blackouts declared yet.
          </div>
        </FadeIn>
      ) : (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))',
              gap: 16,
            }}
          >
            {rows.map((b, i) => (
              <FadeIn key={b.id} rise={4} delayMs={Math.min(i, 6) * 30} duration="slow">
                <BlackoutCard
                  blackout={b}
                  teamsById={teamsById}
                  escalationTargets={escalationTargetsQuery.data}
                  onEdit={() => setEditing(b)}
                  onDelete={() => {
                    if (
                      window.confirm(
                        `Delete blackout "${b.name}"? This cannot be undone.`,
                      )
                    )
                      deleteMutation.mutate(b.id)
                  }}
                />
              </FadeIn>
            ))}
          </div>
          <PaginationControls
            page={pagedList.page}
            pageSize={pagedList.pageSize}
            total={pagedList.total}
            totalPages={pagedList.totalPages}
            pageSizeOptions={pagedList.pageSizeOptions}
            rangeStart={pagedList.rangeStart}
            rangeEnd={pagedList.rangeEnd}
            canPrev={pagedList.canPrev}
            canNext={pagedList.canNext}
            onPageChange={pagedList.setPage}
            onPageSizeChange={pagedList.setPageSize}
            isFetching={pagedList.isFetching}
            standalone
          />
        </>
      )}

      <Modal open={creating} onClose={() => setCreating(false)} title="New blackout">
        {creating ? (
          <BlackoutForm
            mode="create"
            teams={teamsQuery.data ?? []}
            onClose={() => setCreating(false)}
            onSaved={() => {
              setCreating(false)
              void queryClient.invalidateQueries({ queryKey: ['blackouts'] })
              void queryClient.invalidateQueries({ queryKey: ['calendar'] })
            }}
          />
        ) : null}
      </Modal>

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title="Edit blackout"
      >
        {editing ? (
          <BlackoutForm
            mode="edit"
            existing={editing}
            teams={teamsQuery.data ?? []}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null)
              void queryClient.invalidateQueries({ queryKey: ['blackouts'] })
              void queryClient.invalidateQueries({ queryKey: ['calendar'] })
            }}
          />
        ) : null}
      </Modal>

      </div>
    </HrAccessGuard>
  )
}
